import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Slider, Space } from 'antd';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from '../../../helpers/axiosConfig';
import './AdvancedPetitionFilters.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const AdvancedPetitionFilters = ({ onFilterResults }) => {
  const { t } = useTranslation('global');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [votesRange, setVotesRange] = useState([0, 10000]);
  
  // Получаем данные пользователя для проверки роли
  const user = useSelector((state) => state.account.userData);
  const isAdmin = user?.is_staff || user?.is_superuser;

  const statusOptions = [
    { value: '', label: t('filters.allStatuses') },
    { value: 'Опубликована', label: t('status.published') },
    { value: 'На модерации', label: t('status.moderation') },
    { value: 'Завершенная', label: t('status.completed') },
    { value: 'Удалена', label: t('status.deleted') }
  ];

  const sortOptions = [
    { value: '', label: t('filters.default') },
    { value: 'votes_desc', label: t('filters.moreVotes') },
    { value: 'votes_asc', label: t('filters.lessVotes') },
    { value: 'date_desc', label: t('dropDown.new') },
    { value: 'date_asc', label: t('dropDown.old') }
  ];

  const handleSearch = async (values) => {
    setLoading(true);
    
    try {
      const params = {
        status: values.status || undefined,
        search: values.search || undefined,
        min_votes: votesRange[0] || undefined,
        max_votes: votesRange[1] || undefined,
        date_from: values.dateRange?.[0]?.format('YYYY-MM-DD') || undefined,
        date_to: values.dateRange?.[1]?.format('YYYY-MM-DD') || undefined,
        sort_by: values.sort_by || undefined
      };

      // Удаляем undefined значения
      Object.keys(params).forEach(key => 
        params[key] === undefined && delete params[key]
      );

      const response = await axios.get('/petition-filters/advanced_search/', { params });
      
      // Backend возвращает paginated response: { count, next, previous, results }
      // Передаем только results (массив петиций)
      if (response.data && response.data.results) {
        onFilterResults(response.data.results);
      } else {
        onFilterResults([]);
      }
    } catch (error) {
      console.error(t('filters.filterError'), error);
      onFilterResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setVotesRange([0, 10000]);
    onFilterResults(null); // null означает "показать все петиции"
  };

  return (
    <Card className="advanced-filters-card">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSearch}
        initialValues={{
          status: '',
          sort_by: ''
        }}
      >
        <Row gutter={[16, 16]}>
          {/* Поиск */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              name="search"
              label={t('filters.textSearch')}
            >
              <Input
                prefix={<SearchOutlined />}
                placeholder={t('filters.titleOrDescription')}
                allowClear
              />
            </Form.Item>
          </Col>

          {/* Статус - только для админов */}
          {isAdmin && (
            <Col xs={24} md={12} lg={8}>
              <Form.Item
                name="status"
                label={t('filters.petitionStatus')}
              >
                <Select placeholder={t('filters.selectStatus')}>
                  {statusOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}

          {/* Сортировка */}
          <Col xs={24} md={12} lg={8}>
            <Form.Item
              name="sort_by"
              label={t('filters.sort')}
            >
              <Select placeholder={t('filters.selectSort')}>
                {sortOptions.map(opt => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Диапазон голосов */}
          <Col xs={24} md={12}>
            <Form.Item
              label={t('filters.votesCount', { min: votesRange[0], max: votesRange[1] })}
            >
              <Slider
                range
                min={0}
                max={10000}
                step={50}
                value={votesRange}
                onChange={setVotesRange}
                tooltip={{ formatter: (value) => t('filters.votes', { count: value }) }}
              />
            </Form.Item>
          </Col>

          {/* Диапазон дат */}
          <Col xs={24} md={12}>
            <Form.Item
              name="dateRange"
              label={t('filters.creationPeriod')}
            >
              <RangePicker
                style={{ width: '100%' }}
                placeholder={[t('filters.dateFrom'), t('filters.dateTo')]}
                format="DD.MM.YYYY"
              />
            </Form.Item>
          </Col>

          {/* Кнопки */}
          <Col xs={24}>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SearchOutlined />}
                loading={loading}
              >
                {t('filters.applyFilters')}
              </Button>
              <Button
                icon={<ClearOutlined />}
                onClick={handleReset}
              >
                {t('filters.reset')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

export default AdvancedPetitionFilters;
